import { isRetiredBuiltinMap } from './jumpMapRevision.js';

/**
 * Best-ever stars-and-cups progression model per (user, map). Lives
 * in shared/ because both client (HUD, homepage, results overlay)
 * and server (DB upsert, /api/me/progression) need to agree on the
 * milestone thresholds and tier counts.
 */

/** Maximum stars earnable per map (loop 1). */
export const MAX_STARS = 3;

/** Maximum cups earnable per map (loops 2 + 3). */
export const MAX_CUPS = 3;

/**
 * Start and end speed multiplier for one loop. Each loop currently
 * holds a flat speed (start === end), but we keep the ramp shape so
 * the audio engine can continue scheduling a `linearRampToValueAtTime`
 * uniformly — a flat ramp just collapses to a constant.
 */
export interface LoopSpeedRamp {
  startSpeed: number;
  endSpeed: number;
}

/** How much faster each repeat plays compared to the previous one. */
const LOOP_SPEED_INCREMENT_PER_LOOP = 0.25;

/**
 * Flat speed multiplier for the given loop. Loop 0 is the original
 * play at native tempo; every repeat after that steps up by
 * {@link LOOP_SPEED_INCREMENT_PER_LOOP}. Infinite mode keeps calling
 * this with ever-larger indices and the value climbs without limit —
 * the audio engine only enforces a positive floor (`clampPlaybackRate`),
 * so there's no upper plateau: deep loops pitch the music up and
 * eventually outpace the player, which is the intended endgame.
 */
export function loopSpeedRamp(loopIndex: number): LoopSpeedRamp {
  const speed = 1.0 + LOOP_SPEED_INCREMENT_PER_LOOP * Math.max(0, loopIndex);
  return { startSpeed: speed, endSpeed: speed };
}

/** Convenience: how many stars are earned at progress fraction (0..1) during loop 1. */
export function starsForLoop1Progress(fraction: number): number {
  if (fraction >= 1) return 3;
  if (fraction >= 2 / 3) return 2;
  if (fraction >= 1 / 3) return 1;
  return 0;
}

/**
 * How many cups are earned at the given absolute global loop progress.
 * `globalProgress` is the player's total progress through the run as a
 * multiple of one chart length: 0 at the very start of loop 1, 1.0 at
 * the end of loop 1, 1.5 at halfway through loop 2 ("1.5 songs total"),
 * 2.0 at the end of loop 2, 3.0 at the end of loop 3.
 */
export function cupsForGlobalProgress(globalProgress: number): number {
  if (globalProgress >= 3) return 3;
  if (globalProgress >= 2) return 2;
  if (globalProgress >= 1.5) return 1;
  return 0;
}

/**
 * Which gameplay system a map belongs to. Re-declared here (rather than
 * imported from `chart.ts`) so progression code stays shape-only and
 * doesn't pull the whole chart graph into client/server boundaries.
 */
export type ProgressionMode = 'pulse' | 'circles' | 'piano' | 'guitar' | 'waves' | 'jump';

/** A user's best-ever progression on a single map. */
export interface MapProgression {
  /**
   * Slug of the map (stable across DB row regenerations and built-in re-seeds).
   * NOTE: not unique on its own — the same slug can exist in Pulse,
   * Circles, Piano, Guitar, Waves, and Jump. Always pair with {@link mode}
   * when looking up.
   */
  mapSlug: string;
  /**
   * Which mode this progression row belongs to. Required because the
   * five modes share slugs (`on-and-on` exists in each); without
   * `mode` the home-page trophy strip can show the wrong mode's
   * stars/cups.
   */
  mode: ProgressionMode;
  /** Only current official maps contribute to competitive totals. */
  competitiveEligible: boolean;
  stars: number;
  cups: number;
}

export function isCompetitiveMap(map: { isBuiltin: boolean; mode: string; slug: string }): boolean {
  return map.isBuiltin && !isRetiredBuiltinMap(map.mode, map.slug);
}

/** All progression for the signed-in user plus pre-summed totals for the TopNav chip. */
export interface UserProgression {
  perMap: MapProgression[];
  totals: { stars: number; cups: number };
}

/**
 * Sum stars/cups for one mode out of a {@link UserProgression.perMap}.
 *
 * The TopNav uses this to render the per-mode breakdown row beneath
 * the cross-mode totals (e.g. "Pulse: ★15 🏆4"). Done in shared/ so
 * server-side debug pages and tests can compute identical numbers.
 */
export function summariseProgressionByMode(
  perMap: ReadonlyArray<MapProgression>,
  mode: ProgressionMode,
): { stars: number; cups: number } {
  let stars = 0;
  let cups = 0;
  for (const m of perMap) {
    if (m.mode === mode && m.competitiveEligible) {
      stars += m.stars;
      cups += m.cups;
    }
  }
  return { stars, cups };
}

/**
 * Identifies one of the six in-run reward beats. The 3rd star is the
 * mode-transition moment (pause + "INFINITE MODE" card); every other
 * milestone is a non-blocking flourish.
 */
export type MilestoneKind =
  | 'star_1'
  | 'star_2'
  | 'star_3'
  | 'cup_1'
  | 'cup_2'
  | 'cup_3';

export function milestoneIsStar(kind: MilestoneKind): boolean {
  return kind === 'star_1' || kind === 'star_2' || kind === 'star_3';
}

export function milestoneTier(kind: MilestoneKind): 1 | 2 | 3 {
  switch (kind) {
    case 'star_1':
    case 'cup_1':
      return 1;
    case 'star_2':
    case 'cup_2':
      return 2;
    case 'star_3':
    case 'cup_3':
      return 3;
  }
}
